import React from 'react';

export interface TabItem { id: string; label?: string; }

export type TabStyle = 'underline' | 'pills' | 'boxed';
export type TabAlign = 'start' | 'center' | 'end' | 'stretch';

/**
 * Tabbed CONTAINER. Renders the tab bar (controlled — the active tab lives in
 * the page/canvas so the render loop can show/hide member blocks). The content
 * region below the bar is where blocks assigned to the active tab overlay
 * (they're real canvas blocks positioned on top, not children of this DOM).
 * Shared by BlockRenderer (canvas) and PublicCampaign (live) for 1:1 parity.
 */
export const TabsBlock: React.FC<{
  tabs: TabItem[];
  activeTabId: string;
  onSelect?: (tabId: string) => void;
  activeColor?: string;
  inactiveColor?: string;
  barBg?: string;
  tabStyle?: TabStyle;
  tabAlign?: TabAlign;
  tabRadius?: number;
  tabFontSize?: number;
  /** underline style: thickness of the active line + the bar line. */
  underlineThickness?: number;
  /** underline style: colour of the full-width bar line (under all tabs). */
  barLineColor?: string;
  /** Optional hint shown in the (otherwise empty) region — editor only. */
  hint?: string;
}> = ({
  tabs, activeTabId, onSelect,
  activeColor = '#2563eb', inactiveColor = '#6b7280', barBg = 'transparent',
  tabStyle = 'underline', tabAlign = 'start', tabRadius = 999, tabFontSize = 15,
  underlineThickness = 2, barLineColor = '#e5e7eb', hint,
}) => {
  const list = (tabs && tabs.length) ? tabs : [{ id: 't1', label: 'טאב 1' }];
  const activeId = activeTabId || list[0].id;

  const tabStyleFor = (on: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      padding: '8px 16px', cursor: 'pointer', whiteSpace: 'nowrap',
      fontSize: tabFontSize, fontWeight: on ? 700 : 600, transition: 'all .15s',
      border: 'none', background: 'transparent', flex: tabAlign === 'stretch' ? 1 : undefined,
    };
    if (tabStyle === 'pills') {
      return { ...base, borderRadius: tabRadius, background: on ? activeColor : 'transparent', color: on ? '#fff' : inactiveColor };
    }
    if (tabStyle === 'boxed') {
      return { ...base, borderRadius: tabRadius, border: `1px solid ${on ? activeColor : '#e5e7eb'}`, background: on ? activeColor : '#fff', color: on ? '#fff' : inactiveColor };
    }
    // underline
    return { ...base, color: on ? activeColor : inactiveColor, borderBottom: `${underlineThickness}px solid ${on ? activeColor : 'transparent'}`, marginBottom: -underlineThickness };
  };

  const justify = tabAlign === 'center' ? 'center' : tabAlign === 'end' ? 'flex-end' : 'flex-start';

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Tab bar — overflowY pinned to hidden so setting overflowX:auto doesn't
          make the browser show a stray vertical scrollbar; scrollbar hidden. */}
      <div
        className="no-scrollbar"
        style={{
          display: 'flex',
          gap: tabStyle === 'underline' ? 2 : 6,
          justifyContent: justify,
          background: barBg,
          borderBottom: tabStyle === 'underline' && barLineColor !== 'transparent' ? `${underlineThickness}px solid ${barLineColor}` : 'none',
          padding: tabStyle === 'underline' ? 0 : '4px 2px',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          flexShrink: 0,
        }}
      >
        {list.map((t) => {
          const on = t.id === activeId;
          return (
            <button
              key={t.id}
              type="button"
              // Stop mousedown reaching the canvas block wrapper, otherwise it
              // starts selecting/dragging the whole tabs block before the click
              // registers — so the tab would never switch in the editor.
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onSelect?.(t.id); }}
              style={tabStyleFor(on)}
            >
              {t.label || 'טאב'}
            </button>
          );
        })}
      </div>
      {/* Content region — member blocks overlay here from the canvas. */}
      {hint && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 12, pointerEvents: 'none' }}>
          {hint}
        </div>
      )}
    </div>
  );
};
